import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import { Typography } from "@mui/material";
import {
  FileText, Image, Upload, CheckCircle,
  Download, Trash2, RefreshCw
} from "lucide-react";
import OfferLetterGenerator from "./OfferLetterGenerator";
import { useTheme } from "context/ThemeContext";

import { apiBaseUrl } from '../../../../../../services/config';
const API_BASE = apiBaseUrl;

const ax = () => ({
  Authorization: `Bearer ${
    sessionStorage.getItem("platform_token") ||
    localStorage.getItem("platform_token") || ""
  }`
});

// ── OTHER DOCUMENT TEMPLATES ─────────────────────────────────────
const OTHER_DOCS = [
  {
    id:"experience", icon:"📋", title:"Experience Letter",
    desc:"Confirm employment tenure and role for relieving employees",
    fields:["employee_name","position","department","joining_date","last_date","company_name","hr_name"],
    template:(v)=>`
      <div style="font-family:Georgia,serif;max-width:700px;margin:auto;padding:50px 60px;color:#111;">
        ${v.logo ? `<div style="text-align:center;margin-bottom:20px;"><img src="${v.logo}" style="height:60px;object-fit:contain;"/></div>` : ""}
        <h2 style="text-align:center;color:#0D1B2E;margin:0 0 4px;">${v.company_name||"Company Name"}</h2>
        <hr style="border:none;border-top:2px solid #0D1B2E;margin:10px 0 30px;"/>
        <h3 style="text-align:center;text-decoration:underline;letter-spacing:1px;">EXPERIENCE LETTER</h3>
        <p style="text-align:right;">Date: ${new Date().toLocaleDateString("en-IN",{day:"2-digit",month:"long",year:"numeric"})}</p>
        <p>To Whom It May Concern,</p>
        <p>This is to certify that <strong>${v.employee_name||"[Name]"}</strong> was employed with <strong>${v.company_name||"[Company]"}</strong> as <strong>${v.position||"[Position]"}</strong> in the <strong>${v.department||"[Department]"}</strong> department from <strong>${v.joining_date||"[Date]"}</strong> to <strong>${v.last_date||"[Date]"}</strong>.</p>
        <p>During their tenure, they demonstrated professionalism and dedication. We wish them the very best in their future endeavours.</p>
        <br/><br/>
        <p>Sincerely,</p>
        <p><strong>${v.hr_name||"HR Manager"}</strong><br/>Human Resources<br/>${v.company_name||""}</p>
      </div>`,
  },
  {
    id:"relieving", icon:"🚪", title:"Relieving Letter",
    desc:"Formally relieve an employee from their duties",
    fields:["employee_name","position","last_date","company_name","hr_name"],
    template:(v)=>`
      <div style="font-family:Georgia,serif;max-width:700px;margin:auto;padding:50px 60px;color:#111;">
        ${v.logo ? `<div style="text-align:center;margin-bottom:20px;"><img src="${v.logo}" style="height:60px;object-fit:contain;"/></div>` : ""}
        <h2 style="text-align:center;color:#0D1B2E;">${v.company_name||"Company Name"}</h2>
        <hr style="border:none;border-top:2px solid #0D1B2E;margin:10px 0 30px;"/>
        <h3 style="text-align:center;text-decoration:underline;">RELIEVING LETTER</h3>
        <p style="text-align:right;">Date: ${new Date().toLocaleDateString("en-IN",{day:"2-digit",month:"long",year:"numeric"})}</p>
        <p>Dear <strong>${v.employee_name||"[Name]"}</strong>,</p>
        <p>This is to confirm that you have been relieved from your position as <strong>${v.position||"[Position]"}</strong> at <strong>${v.company_name||"[Company]"}</strong> effective <strong>${v.last_date||"[Date]"}</strong>.</p>
        <p>We confirm that all company assets have been returned and dues settled. We wish you all the best in your future endeavours.</p>
        <br/><br/>
        <p>Regards,</p>
        <p><strong>${v.hr_name||"HR Manager"}</strong><br/>Human Resources<br/>${v.company_name||""}</p>
      </div>`,
  },
  {
    id:"appointment", icon:"🤝", title:"Appointment Letter",
    desc:"Formal appointment confirming employment after probation",
    fields:["employee_name","position","department","joining_date","ctc_annual","company_name","hr_name"],
    template:(v)=>`
      <div style="font-family:Georgia,serif;max-width:700px;margin:auto;padding:50px 60px;color:#111;">
        ${v.logo ? `<div style="text-align:center;margin-bottom:20px;"><img src="${v.logo}" style="height:60px;object-fit:contain;"/></div>` : ""}
        <h2 style="text-align:center;color:#0D1B2E;">${v.company_name||"Company Name"}</h2>
        <hr style="border:none;border-top:2px solid #0D1B2E;margin:10px 0 30px;"/>
        <h3 style="text-align:center;text-decoration:underline;">APPOINTMENT LETTER</h3>
        <p style="text-align:right;">Date: ${new Date().toLocaleDateString("en-IN",{day:"2-digit",month:"long",year:"numeric"})}</p>
        <p>Dear <strong>${v.employee_name||"[Name]"}</strong>,</p>
        <p>Following your successful completion of the probation period, we are pleased to confirm your appointment as <strong>${v.position||"[Position]"}</strong> in the <strong>${v.department||"[Department]"}</strong> department of <strong>${v.company_name||"[Company]"}</strong> with effect from <strong>${v.joining_date||"[Date]"}</strong>.</p>
        <p>Your annual CTC will be <strong>${v.ctc_annual||"[CTC]"}</strong> as per the compensation structure communicated to you.</p>
        <p>We look forward to your continued contribution to the organisation.</p>
        <br/><br/>
        <p>Sincerely,</p>
        <p><strong>${v.hr_name||"HR Manager"}</strong><br/>Human Resources<br/>${v.company_name||""}</p>
      </div>`,
  },
  {
    id:"noc", icon:"✅", title:"No Objection Certificate",
    desc:"Issue NOC for employees applying for loans, visa, or higher education",
    fields:["employee_name","position","joining_date","purpose","company_name","hr_name"],
    template:(v)=>`
      <div style="font-family:Georgia,serif;max-width:700px;margin:auto;padding:50px 60px;color:#111;">
        ${v.logo ? `<div style="text-align:center;margin-bottom:20px;"><img src="${v.logo}" style="height:60px;object-fit:contain;"/></div>` : ""}
        <h2 style="text-align:center;color:#0D1B2E;">${v.company_name||"Company Name"}</h2>
        <hr style="border:none;border-top:2px solid #0D1B2E;margin:10px 0 30px;"/>
        <h3 style="text-align:center;text-decoration:underline;">NO OBJECTION CERTIFICATE</h3>
        <p style="text-align:right;">Date: ${new Date().toLocaleDateString("en-IN",{day:"2-digit",month:"long",year:"numeric"})}</p>
        <p>To Whom It May Concern,</p>
        <p>This is to certify that <strong>${v.employee_name||"[Name]"}</strong> is currently employed with <strong>${v.company_name||"[Company]"}</strong> as <strong>${v.position||"[Position]"}</strong> since <strong>${v.joining_date||"[Date]"}</strong>.</p>
        <p>We have no objection to ${v.employee_name||"the employee"} ${v.purpose||"pursuing the stated purpose"}.</p>
        <p>This certificate is issued on request and without prejudice.</p>
        <br/><br/>
        <p>Yours faithfully,</p>
        <p><strong>${v.hr_name||"HR Manager"}</strong><br/>Human Resources<br/>${v.company_name||""}</p>
      </div>`,
  },
];

/* ── Document form field labels ──────────────────────────────────── */
const FIELD_LABELS = {
  employee_name:"Employee Full Name", position:"Position / Designation",
  department:"Department", joining_date:"Date of Joining",
  last_date:"Last Working Day", company_name:"Company Name",
  hr_name:"HR Signatory Name", ctc_annual:"Annual CTC",
  purpose:"Purpose (e.g. bank loan, visa)",
};

/* ══ TAB: Company Logo ═══════════════════════════════════════════ */
const LogoTab = () => {
  const { T } = useTheme();
  const [logo,       setLogo]     = useState(null);
  const [preview,    setPreview]  = useState(null);
  const [saving,     setSaving]   = useState(false);
  const [toast,      setToast]    = useState("");
  const fileRef = useRef();

  const flash = (msg) => { setToast(msg); setTimeout(()=>setToast(""),3500); };

  useEffect(()=>{
    axios.get(`${API_BASE}/companies/my`, { headers: ax() })
      .then(r=>{ if(r.data?.logo_url) setPreview(r.data.logo_url); })
      .catch(()=>{});
  },[]);

  const onFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2*1024*1024) { flash("❌ Image must be under 2MB"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setLogo(ev.target.result);   // base64 data URL
      setPreview(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!logo) return;
    setSaving(true);
    try {
      await axios.patch(
        `${API_BASE}/companies/my/logo`,
        { logo_url: logo },
        { headers: ax() }
      );
      // Persist logo for use across the app this session
      sessionStorage.setItem("company_logo", logo);
      localStorage.setItem("company_logo", logo);
      flash("✅ Logo saved — appears in offer letters and payslips");
    } catch(e) {
      flash("❌ " + (e.response?.data?.detail || "Save failed"));
    } finally { setSaving(false); }
  };

  const removeLogo = async () => {
    try {
      await axios.patch(`${API_BASE}/companies/my/logo`,
        { logo_url: null }, { headers: ax() });
      setLogo(null); setPreview(null);
      sessionStorage.removeItem("company_logo");
      localStorage.removeItem("company_logo");
      flash("✅ Logo removed");
    } catch { flash("❌ Remove failed"); }
  };

  return (
    <div style={{ maxWidth:640 }}>
      <div style={{ marginBottom:24 }}>
        <div style={{ fontSize:13, color:T.muted, lineHeight:1.7 }}>
          Upload your company logo. It will automatically appear in:
          <ul style={{ marginTop:8, paddingLeft:20 }}>
            {["Offer Letters","Payslips","Experience Letters","Relieving Letters",
              "Appointment Letters","No Objection Certificates"].map(d=>(
              <li key={d} style={{ color:T.text, marginBottom:3 }}>{d}</li>
            ))}
          </ul>
        </div>
      </div>

      {/* Preview */}
      <div style={{ padding:24, background:T.card2,
        border:`2px dashed ${preview?T.green:T.border}`,
        borderRadius:12, marginBottom:20, textAlign:"center",
        cursor:"pointer", minHeight:140,
        display:"flex", flexDirection:"column",
        alignItems:"center", justifyContent:"center" }}
        onClick={()=>fileRef.current.click()}>
        {preview ? (
          <>
            <img src={preview} alt="Company Logo"
              style={{ maxHeight:100, maxWidth:280, objectFit:"contain",
                marginBottom:12, borderRadius:6 }}/>
            <div style={{ fontSize:12, color:T.green }}>
              ✓ Logo uploaded — click to replace
            </div>
          </>
        ) : (
          <>
            <Upload size={32} style={{ color:T.dim, marginBottom:10 }}/>
            <div style={{ fontSize:14, color:T.muted, marginBottom:4 }}>
              Click to upload company logo
            </div>
            <div style={{ fontSize:12, color:T.dim }}>
              PNG, JPG or SVG · Max 2MB · Recommended: 300×100px
            </div>
          </>
        )}
      </div>

      <input ref={fileRef} type="file" accept="image/*"
        style={{ display:"none" }} onChange={onFile}/>

      <div style={{ display:"flex", gap:10 }}>
        <button onClick={()=>fileRef.current.click()}
          style={{ flex:1, padding:"10px", borderRadius:9,
            border:`1px solid ${T.border}`, background:"none",
            color:T.muted, cursor:"pointer",
            fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700 }}>
          <Upload size={14} style={{ display:"inline", marginRight:6 }}/>
          Choose File
        </button>
        {logo && (
          <button onClick={save} disabled={saving}
            style={{ flex:2, padding:"10px", borderRadius:9, border:"none",
              background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
              color:"#fff", cursor:"pointer",
              fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:800,
              opacity:saving?.7:1 }}>
            {saving ? "Saving…" : "✓ Save Logo"}
          </button>
        )}
        {preview && !logo && (
          <button onClick={removeLogo}
            style={{ padding:"10px 18px", borderRadius:9,
              border:`1px solid ${T.red}30`, background:"none",
              color:T.red, cursor:"pointer",
              fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700 }}>
            <Trash2 size={13} style={{ display:"inline", marginRight:4 }}/>
            Remove
          </button>
        )}
      </div>

      {/* Usage tip */}
      <div style={{ marginTop:20, padding:"12px 16px",
        background:`${T.cyan}08`, border:`1px solid ${T.border}`,
        borderRadius:9, fontSize:12, color:T.muted, lineHeight:1.6 }}>
        💡 <strong style={{color:T.text}}>Tip:</strong> Logo is stored securely with your company profile.
        It is automatically included in document headers without any manual steps.
        For best results use a transparent background PNG at 300×100px.
      </div>

      {toast && (
        <div style={{ marginTop:12, padding:"10px 16px", borderRadius:9,
          background:toast.startsWith("✅")?`${T.green}10`:`${T.red}10`,
          border:`1px solid ${toast.startsWith("✅")?T.green:T.red}30`,
          color:toast.startsWith("✅")?T.green:T.red,
          fontSize:13, fontWeight:600 }}>
          {toast}
        </div>
      )}
    </div>
  );
};

/* ══ TAB: Other Documents ════════════════════════════════════════ */
const OtherDocsTab = () => {
  const { T, theme } = useTheme();
  const [active,  setActive]  = useState(null);  // selected doc template
  const [fields,  setFields]  = useState({});
  const [preview, setPreview] = useState("");

  const logo = sessionStorage.getItem("company_logo") ||
               localStorage.getItem("company_logo") || null;

  const setF = (k,v) => setFields(f=>({...f,[k]:v}));

  const generate = () => {
    const vars = { ...fields, logo };
    setPreview(active.template(vars));
  };

  const print = () => {
    if (!preview) { generate(); return; }
    const w = window.open("","_blank");
    w.document.write(`<!DOCTYPE html><html><head>
      <title>${active.title}</title>
      <style>body{margin:0;padding:0;}@media print{button{display:none;}}</style>
    </head><body>${preview}</body></html>`);
    w.document.close();
    setTimeout(()=>w.print(), 500);
  };

  return (
    <div style={{ display:"grid", gridTemplateColumns: active ? "320px 1fr" : "1fr",
      gap:20, alignItems:"start" }}>

      {/* Template cards */}
      <div>
        <div style={{ fontSize:11, color:T.muted, fontWeight:700,
          textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:14 }}>
          Select Document Type
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {OTHER_DOCS.map(doc=>(
            <div key={doc.id}
              onClick={()=>{ setActive(doc); setFields({}); setPreview(""); }}
              style={{ padding:"14px 16px", borderRadius:10, cursor:"pointer",
                border:`1px solid ${active?.id===doc.id?T.cyan:T.border}`,
                background:active?.id===doc.id?`${T.cyan}14`:T.card2,
                transition:"all .2s" }}>
              <div style={{ fontSize:18, marginBottom:4 }}>{doc.icon}</div>
              <div style={{ fontSize:13.5, fontWeight:700,
                color:active?.id===doc.id?T.cyan:T.text }}>
                {doc.title}
              </div>
              <div style={{ fontSize:12, color:T.muted, marginTop:2,
                lineHeight:1.5 }}>
                {doc.desc}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Form + Preview */}
      {active && (
        <div>
          {/* Fields */}
          <div style={{ padding:"16px 20px", background:T.card2,
            borderRadius:12, border:`1px solid ${T.border}`, marginBottom:16 }}>
            <div style={{ fontSize:12, fontWeight:700, color:T.cyan,
              textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:14 }}>
              {active.icon} {active.title} — Fill Details
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              {active.fields.map(f=>(
                <div key={f}>
                  <label style={{ fontSize:11, fontWeight:700, color:T.muted,
                    textTransform:"uppercase", letterSpacing:"0.07em",
                    display:"block", marginBottom:4 }}>
                    {FIELD_LABELS[f]||f}
                  </label>
                  <input
                    type={f.includes("date")?"date":"text"}
                    value={fields[f]||""}
                    onChange={e=>setF(f,e.target.value)}
                    style={{ width:"100%", padding:"8px 12px",
                      border:`1px solid ${T.border}`, borderRadius:7,
                      background:T.inp_bg, color:T.text,
                      fontFamily:"Outfit,sans-serif", fontSize:13,
                      outline:"none", boxSizing:"border-box",
                      colorScheme:f.includes("date")?theme:undefined }}/>
                </div>
              ))}
            </div>
            <div style={{ marginTop:14, display:"flex", gap:10 }}>
              <button onClick={generate}
                style={{ padding:"9px 20px", borderRadius:8,
                  border:`1px solid ${T.cyan}30`,
                  background:`${T.cyan}14`,
                  color:T.cyan, cursor:"pointer",
                  fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700 }}>
                <Eye size={13} style={{display:"inline",marginRight:5}}/>Preview
              </button>
              <button onClick={print}
                style={{ padding:"9px 20px", borderRadius:8, border:"none",
                  background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
                  color:"#fff", cursor:"pointer",
                  fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:800 }}>
                🖨️ Print / Save PDF
              </button>
            </div>
          </div>

          {/* Preview */}
          {preview && (
            <div style={{ background:"#e8e8e8", borderRadius:12, padding:16 }}>
              <div style={{ background:"#fff", borderRadius:4,
                boxShadow:"0 4px 24px rgba(0,0,0,0.12)",
                overflow:"hidden" }}
                dangerouslySetInnerHTML={{ __html: preview }}/>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ══ MAIN Documents PAGE ═════════════════════════════════════════ */
const Documents = () => {
  const { T } = useTheme();
  const location = useLocation();
  const [tab, setTab] = useState(() => {
    // Read initial tab from URL path
    if (location.pathname.includes("offer-letters")) return "offer";
    if (location.pathname.includes("other"))         return "other";
    if (location.pathname.includes("branding"))      return "logo";
    return "offer";
  });
  const [offerIv, setOfferIv] = useState(null);
  const [toast,   setToast]   = useState("");

  const flash = (msg) => { setToast(msg); setTimeout(()=>setToast(""),3500); };

  const TABS = [
    // { key:"offer",  icon:"📄", label:"Offer Letters" },
    // { key:"other",  icon:"📋", label:"Other Documents" },
    { key:"logo",   icon:"🏢", label:"Logo & Branding" },  
  ];

  const ax_hdr = { headers: ax() };

  return (
    <div style={{ fontFamily:"Outfit,sans-serif", color:T.text, padding:0 }}>

      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb:2 }}>
        <Link to="/admin" style={{ textDecoration:"none", color:T.muted, fontSize:12 }}>
          Admin
        </Link>
        <Typography sx={{ fontSize:12, color:T.muted }}>Documents</Typography>
      </Breadcrumbs>

      {/* Header */}
      <div style={{ marginBottom:24 }}>
        <div style={{ fontSize:11, color:T.cyan, fontWeight:700,
          letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:4 }}>
          DOCUMENTS
        </div>
        <h2 style={{ fontSize:22, fontWeight:800, margin:"0 0 4px", color:T.text }}>
          Document Management
        </h2>
        <p style={{ fontSize:13.5, color:T.muted, margin:0 }}>
          Generate, customise and send HR documents with your company branding
        </p>
      </div>

      {/* Tab bar */}
      <div style={{ display:"flex", gap:4,
        borderBottom:`1px solid ${T.border}`, marginBottom:24 }}>
        {TABS.map(t=>(
          <button key={t.key} onClick={()=>setTab(t.key)}
            style={{ padding:"10px 20px", borderRadius:"8px 8px 0 0",
              border:`1px solid ${tab===t.key?T.border:"transparent"}`,
              borderBottom:"none",
              background:tab===t.key?T.card:T.card2,
              color:tab===t.key?T.cyan:T.muted, cursor:"pointer",
              fontFamily:"Outfit,sans-serif", fontSize:13, fontWeight:700,
              display:"flex", alignItems:"center", gap:7,
              marginBottom:tab===t.key?-1:0 }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ background:T.card, border:`1px solid ${T.border}`,
        borderRadius:"0 12px 12px 12px", padding:24 }}>

        {/* OFFER LETTERS TAB */}
        {tab==="offer" && (
          <>
            <div style={{ fontSize:13, color:T.muted, marginBottom:20,
              lineHeight:1.6 }}>
              Generate fully customisable offer letters for selected candidates.
              Select a candidate from the <strong style={{color:T.text}}>Interviews → Selected</strong> tab
              and click the 📄 button, or create a standalone offer letter below.
            </div>

            {/* Quick-generate card */}
            <div style={{ padding:"20px 24px", background:T.card2,
              border:`1px solid ${T.border}`, borderRadius:12, marginBottom:20 }}>
              <div style={{ fontSize:14, fontWeight:700, color:T.text,
                marginBottom:8 }}>
                📄 New Offer Letter
              </div>
              <div style={{ fontSize:13, color:T.muted, marginBottom:16 }}>
                Create an offer letter for any candidate — even without a linked interview record.
              </div>
              <button
                onClick={()=>setOfferIv({ id:0, first_name:"", last_name:"",
                  position:"", email:"", room_name:"" })}
                style={{ padding:"10px 24px", borderRadius:9, border:"none",
                  background:`linear-gradient(135deg,${T.cyan},${T.purple})`,
                  color:"#fff", fontFamily:"Outfit,sans-serif",
                  fontSize:13, fontWeight:800, cursor:"pointer" }}>
                + Create Offer Letter
              </button>
            </div>

            {/* What's included */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12 }}>
              {[
                { icon:"🏢", title:"Company Branding",
                  desc:"Your logo appears automatically in the header" },
                { icon:"📝", title:"11 Editable Sections",
                  desc:"Offer, compensation, leave, notice period, IP, and more" },
                { icon:"⚡", title:"Dynamic Variables",
                  desc:"Fill once — name, salary, dates auto-populate throughout" },
                { icon:"🖨️", title:"Print / Save PDF",
                  desc:"One-click browser print with A4 formatting" },
                { icon:"📧", title:"Email to Candidate",
                  desc:"Send directly via your SMTP configuration" },
                { icon:"✏️", title:"Fully Editable",
                  desc:"Edit any section text to match your company tone" },
              ].map(f=>(
                <div key={f.title} style={{ padding:"14px 16px",
                  background:T.inp_bg, border:`1px solid ${T.border}`,
                  borderRadius:10 }}>
                  <div style={{ fontSize:20, marginBottom:6 }}>{f.icon}</div>
                  <div style={{ fontSize:13, fontWeight:700, color:T.text,
                    marginBottom:4 }}>{f.title}</div>
                  <div style={{ fontSize:12, color:T.muted, lineHeight:1.5 }}>
                    {f.desc}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab==="other"  && <OtherDocsTab />}
        {tab==="logo"   && <LogoTab />}
      </div>

      {/* Offer Letter Generator modal */}
      {offerIv && (
        <OfferLetterGenerator
          iv={offerIv}
          authHdr={{ headers: ax() }}
          onClose={()=>setOfferIv(null)}
          flash={flash}
        />
      )}

      {toast && (
        <div style={{ position:"fixed", bottom:28, left:"50%",
          transform:"translateX(-50%)", padding:"10px 22px",
          borderRadius:10, fontSize:13, fontWeight:600, zIndex:9999,
          background:T.card,
          border:`1px solid ${toast.startsWith("✅")?`${T.green}4D`:`${T.red}4D`}`,
          color:toast.startsWith("✅")?T.green:T.red,
          boxShadow:T.shadow,
          fontFamily:"Outfit,sans-serif" }}>
          {toast}
        </div>
      )}
    </div>
  );
};

export default Documents;